import React, { useEffect } from 'react';
import FormField from '../../FormField';
import { Text } from 'library';
import JobDatePicker from './JobDatePicker';
import JobDayPicker from './JobDayPicker';
import JobTimePicker from './JobTimePicker';
import JobMultiTimePicker from './JobMultiTimePicker';
import JobSameTimePicker from './JobSameTimePicker';
import BusinessJobDateRangePicker from './BusinessJobDateRangePicker';
import { differenceInDays, isSameDay } from 'date-fns';
import { useUserContext } from 'UserContext';
import { FormFieldProps } from '../../FormTypes';
import { BusinessJobTypes, DayOfWeekSelections, JobTimes, RequestType } from 'parent-portal/forms/JobRequestTypes';
import moment from 'moment';
import BreakRequiredField from './BreakRequiredField';
import _ from 'lodash';

export default function BusinessDatesAndTimes({
    businessJobType,
    requestType,
    startDate,
    endDate,
    daysOfTheWeek,
    sameTimes,
    jobTimes,
    setIsValid,
    breakRequired,
    breakLength,
    slotsAvailable,
}: {
    businessJobType: FormFieldProps<BusinessJobTypes | undefined>;
    requestType: FormFieldProps<RequestType>;
    startDate: FormFieldProps<moment.Moment | null>;
    endDate: FormFieldProps<moment.Moment | null>;
    daysOfTheWeek: FormFieldProps<DayOfWeekSelections>;
    sameTimes: FormFieldProps<boolean>;
    jobTimes: FormFieldProps<JobTimes>;
    setIsValid: (valid: boolean) => void;
    breakRequired: FormFieldProps<boolean>;
    breakLength: FormFieldProps<number | undefined>;
    slotsAvailable: FormFieldProps<number>;
}) {
    const trialRun = businessJobType.value === 'TRIAL_RUN';
    const subJob = businessJobType.value === 'SUBSTITUTE';
    const { getDisplayText } = useUserContext();

    useEffect(() => {
        const validBreak = !breakRequired.value || !!breakLength.value;
        const validTimes = jobTimes.isValid || (!sameTimes.value && daysOfTheWeek.isValid);
        const multiDay = Object.entries(daysOfTheWeek.value).filter((day) => day[1].selected).length > 1;
        const validTrialRun =
            validTimes &&
            validBreak &&
            !!startDate.value &&
            !!startDate.isValid &&
            startDate.value.isSameOrAfter(moment(), 'date');

        const validSubJob =
            validTimes &&
            validBreak &&
            !!startDate.value &&
            !!endDate.value &&
            startDate.isValid &&
            endDate.isValid &&
            startDate.value.isSameOrAfter(moment(), 'date') &&
            (!multiDay ||
                (startDate.value < endDate.value &&
                    differenceInDays(endDate.value.toDate(), startDate.value.toDate()) <= 90));

        if (trialRun) setIsValid(validTrialRun);
        if (subJob) setIsValid(validSubJob);

        requestType.setValue(startDate.value !== endDate.value ? 'recurring' : 'onetime');
    }, [
        jobTimes,
        daysOfTheWeek,
        startDate,
        endDate,
        sameTimes.value,
        trialRun,
        subJob,
        requestType,
        setIsValid,
        breakRequired,
        breakLength,
    ]);

    function updateAllSlots(slots: number) {
        slotsAvailable.setValue(slots);
        const updatedSlots = Object.entries(daysOfTheWeek.value).map(([day, values]) => [day, { ...values, slots }]);
        daysOfTheWeek.setValue(_.fromPairs(updatedSlots) as DayOfWeekSelections);
        jobTimes.setValue({ ...jobTimes.value, slots });
    }

    return (
        <>
            <FormField
                title={subJob ? 'What are the dates/times that you need?' : 'When does the trial run start?'}
                style={{ marginTop: 30 }}
            >
                {subJob ? (
                    <Text variant="body2" textStyle={{ marginTop: 10 }}>
                        {getDisplayText('substitute').capitalize()} shifts can be one single day or multiple days.
                    </Text>
                ) : null}
                {trialRun ? (
                    <FormField style={{ marginTop: 30 }}>
                        <JobDatePicker jobDate={startDate} />
                    </FormField>
                ) : (
                    <FormField title={'Pick the dates.'} style={{ marginTop: 30 }}>
                        <BusinessJobDateRangePicker startDate={startDate} endDate={endDate} />
                    </FormField>
                )}
            </FormField>

            {isSameDay(startDate.value?.toDate() ?? new Date(), endDate.value?.toDate() ?? new Date()) &&
            !trialRun ? null : (
                <FormField title={'Choose the days of the week.'} style={{ marginTop: 30 }}>
                    <JobDayPicker daysOfTheWeek={daysOfTheWeek} />
                </FormField>
            )}
            <FormField title={'Select the times and number of workers per shift.'} style={{ marginTop: 30 }}>
                <JobSameTimePicker isSameTime={sameTimes} daysOfTheWeek={daysOfTheWeek} />
                {sameTimes.value ? (
                    <JobTimePicker
                        jobTimes={jobTimes}
                        startDate={startDate.value}
                        allowPastMidnight={false}
                        slotsAvailable={slotsAvailable}
                        updateAllSlots={updateAllSlots}
                    />
                ) : (
                    <JobMultiTimePicker daysOfTheWeek={daysOfTheWeek} />
                )}
                <BreakRequiredField breakRequired={breakRequired} breakLength={breakLength} />
            </FormField>
        </>
    );
}
